import { useQuery } from '@tanstack/react-query';
import { Printer, Receipt } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { DetalleDelCobro } from '@/shared/api/tipos';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { PanelLateral } from '@/shared/ui/PanelLateral';
import { nombreDelEstadoDePago } from './estados';

const NOMBRE_DEL_MEDIO: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  deposito: 'Depósito',
};

export function PanelDelCobro({
  pagoId,
  abierto,
  alCerrar,
  alImprimir,
  imprimiendo,
}: {
  pagoId: number;
  abierto: boolean;
  alCerrar: () => void;
  alImprimir: () => void;
  imprimiendo: boolean;
}) {
  const detalle = useQuery({
    queryKey: ['cobro-detalle', pagoId],
    queryFn: () => api.get<DetalleDelCobro>(`/cobros/${pagoId}`),
    enabled: abierto,
  });

  const d = detalle.data;

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo="Detalle del cobro"
      descripcion={d?.numeroComprobante ?? ''}
      icono={Receipt}
    >
      {detalle.isPending ? (
        <CargandoEnLinea detalle="Buscando el cobro" />
      ) : (
        d && (
          <>
            <div className="rounded-2xl bg-armazon-900 p-5 text-white">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-white/60">
                Cobrado
              </p>
              <p className="importe mt-1 text-3xl font-bold">${d.total.toFixed(2)}</p>
              <p className="mt-1 text-[11px] uppercase text-white/60">
                Servicio ${d.capital.toFixed(2)}
                {d.interes > 0 && <> · interés ${d.interes.toFixed(2)}</>}
              </p>
            </div>

            <dl className="grid grid-cols-2 gap-4">
              <Dato rotulo="Quién pagó" valor={d.cliente} />
              <Dato rotulo="Identificación" valor={d.identificacion} mono />
              <Dato rotulo="Fecha" valor={enFecha(d.fechaPago)} />
              <Dato
                rotulo="Medio"
                valor={
                  d.medios.length > 1
                    ? d.medios.map((m) => m.medioNombre).join(' y ')
                    : (NOMBRE_DEL_MEDIO[d.metodo] ?? d.metodo)
                }
              />
              <Dato rotulo="Atendió" valor={d.cajero} />
              <Dato rotulo="Caja" valor={d.nombreDeLaCaja ?? 'Serie general'} />
              {d.medios.length === 0 && d.referenciaBancaria && (
                <Dato
                  rotulo={d.metodo === 'cheque' ? 'N.º del cheque' : 'Referencia bancaria'}
                  valor={d.referenciaBancaria}
                  mono
                />
              )}
              {d.medios.length === 0 && d.banco && (
                <Dato rotulo="Banco girador" valor={d.banco} />
              )}
              {d.estado !== 'registrado' && (
                <Dato rotulo="Estado" valor={nombreDelEstadoDePago(d.estado)} />
              )}
            </dl>

            {d.medios.length > 1 && (
              <dl className="divide-y divide-armazon-100 rounded-xl border border-armazon-200">
                {d.medios.map((m) => (
                  <div
                    key={m.medio}
                    className="flex items-baseline justify-between gap-3 px-4 py-2.5"
                  >
                    <dt className="text-[11px] font-semibold uppercase text-armazon-600">
                      {m.medioNombre}
                      {m.referenciaBancaria && (
                        <span className="importe ml-2 font-normal normal-case text-armazon-400">
                          {m.referenciaBancaria}
                        </span>
                      )}
                      {m.banco && (
                        <span className="ml-2 font-normal normal-case text-armazon-400">
                          {m.banco}
                        </span>
                      )}
                    </dt>
                    <dd className="importe text-sm font-bold text-armazon-900">
                      ${m.importe.toFixed(2)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {d.observaciones && (
              <p className="rounded-lg border border-armazon-200 bg-armazon-50 p-3 text-xs text-armazon-700">
                {d.observaciones}
              </p>
            )}

            <div className="overflow-hidden rounded-xl border border-armazon-200">
              <div className="grid grid-cols-[1fr_auto] gap-3 border-b border-armazon-200 bg-armazon-50 px-4 py-2">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
                  Descripción
                  {d.predios.length > 1 && (
                    <span className="ml-1 font-normal normal-case text-armazon-400">
                      ({d.predios.length} predios)
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
                  Monto
                </span>
              </div>

              <div className={d.predios.length > 3 ? 'max-h-80 overflow-y-auto' : undefined}>
              {d.predios.map((predio) => (
                <div key={predio.numeroCuenta ?? 'sin-cuenta'}>
                  <div className="flex items-baseline justify-between gap-3 border-b border-armazon-100 bg-armazon-50/40 px-4 py-2">
                    <span className="min-w-0">
                      <span className="importe block text-[11px] font-bold text-armazon-800">
                        CTA {predio.numeroCuenta ?? 'SIN CUENTA'}
                      </span>
                      <span className="block truncate text-[10px] uppercase text-armazon-500">
                        {predio.direccion || 'Sin dirección registrada'}
                      </span>
                    </span>
                    <span className="importe shrink-0 text-[11px] font-semibold text-armazon-700">
                      ${predio.total.toFixed(2)}
                    </span>
                  </div>

                  {predio.meses.map((mes, i) => (
                    <div
                      key={`${mes.periodo}-${i}`}
                      className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-armazon-100 px-4 py-2 last:border-b-0"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-[11px] uppercase text-armazon-700">
                          {mes.concepto ?? 'Servicio de recolección'}
                        </span>
                        <span className="importe block text-[10px] text-armazon-400">
                          MES: {enMes(mes.periodo)}
                          {mes.historica && ' · SISTEMA ANTERIOR'}
                        </span>
                      </span>
                      <span className="importe text-xs font-semibold text-marca-700">
                        ${mes.total.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}

              </div>

              {d.interes > 0 && (
                <div className="grid grid-cols-[1fr_auto] gap-3 border-t border-armazon-200 px-4 py-2.5">
                  <span className="text-[11px] uppercase text-armazon-700">
                    Interés por mora
                  </span>
                  <span className="importe text-xs font-semibold text-amber-600">
                    ${d.interes.toFixed(2)}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-[1fr_auto] gap-3 border-t border-armazon-200 bg-marca-50 px-4 py-3">
                <span className="text-xs font-bold uppercase text-armazon-800">
                  Total cobrado
                </span>
                <span className="importe text-sm font-bold text-marca-700">
                  ${d.total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="rounded-xl border border-armazon-200 bg-armazon-50/60 p-4 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
                Código de verificación
              </p>
              <p className="importe mt-1 text-lg font-bold tracking-[0.2em] text-armazon-800">
                {d.codigoDeVerificacion}
              </p>
              <p className="mt-1 text-[10px] uppercase leading-relaxed text-armazon-500">
                Va impreso en el recibo. Un papel cuyo código no cuadre con este no salió de
                aquí.
              </p>
            </div>

            <button
              type="button"
              onClick={alImprimir}
              disabled={imprimiendo}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-armazon-200 text-xs font-semibold uppercase text-armazon-600 transition-colors hover:border-marca-300 hover:text-marca-700 disabled:opacity-60"
            >
              <Printer className="size-4" aria-hidden />
              {imprimiendo ? 'Preparando el recibo…' : 'Imprimir recibo de 58 mm'}
            </button>
          </>
        )
      )}
    </PanelLateral>
  );
}

function Dato({ rotulo, valor, mono }: { rotulo: string; valor: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
        {rotulo}
      </dt>
      <dd
        className={`truncate text-xs font-medium uppercase text-armazon-800 ${mono ? 'importe' : ''}`}
      >
        {valor}
      </dd>
    </div>
  );
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}

const MESES = [
  'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
  'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE',
];

function enMes(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  return `${MESES[Number(mes) - 1] ?? mes} ${anio}`;
}
